import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Formik
import { Formik } from "formik";

// Material UI
import { Box, Typography, Fade } from "@mui/material";

// Styles
import { textStyles, commonStyles } from "Common/Styles";

// Components
import { clientValidationSchema } from "Validation";
import {
  GeneralInfoPage,
  LocationPage,
  ReasonsPage,
  NotificationSettingsPage,
  OptionalSettingsPage,
  ReviewPage,
} from "./Pages";

// Common Admin Components / Utils
import {
  NavigationButtons,
  ReviewUpdateDialog,
  PageTabs,
  SuccessDialog,
  lookupStepForError,
  handleNewStepClick,
} from "../Common";

// Resources
import { createClient, updateClient } from "API/Resource/client";
import { getAllClients } from "API/Resource/client";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { setAllClients } from "Store/ClientSlice";
import { selectUserEmail } from "Store/AuthSlice";
import { openSnackbar } from "Store/SnackbarSlice";

// Libraries
import { isEqual } from "lodash";
import { DateTime } from "luxon";
import { MNStepper } from "Common/Components";
import { ConfirmLeaveModal } from "Common/Modals/ConfirmLeaveModal";

// Form default values
const defaultValues = {
  name: "",
  domain: "",
  appointmentLocations: [],
  appointmentReasons: [],
  settings: {
    appointmentLocations: false,
    appointmentReasons: false,
    fullyBookedHelperText: false,
  },
  defaultTextMessages: {},
};

const stepFieldsMap = {
  "General Information": ["name", "domain", "settings"],
  "Appointment Location": ["appointmentLocations"],
  "Appointment Reason for Visit": ["appointmentReasons"],
  "Notification Settings": ["defaultTextMessages"],
};

const ClientSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const userEmail = useSelector(selectUserEmail);
  const editClient = location.state?.client;

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [latestStepCompleted, setLatestStepCompleted] = useState(0);

  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);

  const [completed, setCompleted] = useState(false);

  const stepComponents = useMemo(
    () => ({
      "General Information": (props) => <GeneralInfoPage {...props} />,
      "Appointment Location": (props) => <LocationPage {...props} />,
      "Appointment Reason for Visit": (props) => <ReasonsPage {...props} />,
      "Notification Settings": (props) => (
        <NotificationSettingsPage {...props} />
      ),
      "Optional Settings": (props) => <OptionalSettingsPage {...props} />,
      Review: (props) => <ReviewPage {...props} />,
    }),
    []
  );

  const getClientSteps = (settings) => {
    const steps = ["General Information"];
    if (settings.appointmentLocations) steps.push("Appointment Location");
    if (settings.appointmentReasons) steps.push("Appointment Reason for Visit");
    steps.push("Notification Settings", "Optional Settings", "Review");
    return steps;
  };

  useEffect(() => {
    setPage(0);
  }, [editClient]);

  const navigateToClientManagement = useCallback(() => {
    navigate("/ClientManagement");
  }, [navigate]);

  const handleOpenSnackbar = (message, severity = "error") => {
    dispatch(openSnackbar({ message, severity }));
  };

  const handleClientSave = async (values) => {
    setSaving(true);
    editClient
      ? await handleUpdateClient(values)
      : await handleCreateClient(values);
    setSaving(false);
  };

  const handleCreateClient = async (values) => {
    try {
      await createClient(values);
      // Refetch all clients and update Redux
      const allClients = await getAllClients(false);
      dispatch(setAllClients(allClients));
      setConfirmationOpen(true);
    } catch (error) {
      handleOpenSnackbar(error.message);
    }
  };

  const handleUpdateClient = async (values) => {
    const stripShowInformationFields = (locations) =>
      locations?.map(({ showInformationFields, ...rest }) => rest) || [];

    const changes = Object.keys(values).reduce((acc, key) => {
      if (key === "appointmentLocations") {
        const current = stripShowInformationFields(values[key]);
        const original = stripShowInformationFields(editClient[key]);
        if (!isEqual(current, original)) {
          acc[key] = values[key];
        }
      } else if (!isEqual(values[key], editClient[key])) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    if (Object.keys(changes).length === 0) {
      handleOpenSnackbar(
        "Please make sure to change at least one field to update the client.",
        "info"
      );
    } else {
      setPendingChanges(changes);
      setUpdateDialogOpen(true);
    }
  };

  const processClientChanges = async () => {
    try {
      if (Object.keys(pendingChanges).length > 0) {
        let changesToSend = { ...pendingChanges };

        // take away the showInformationFields
        if (changesToSend.appointmentLocations) {
          changesToSend.appointmentLocations =
            changesToSend.appointmentLocations.map(
              ({ showInformationFields, ...rest }) => rest
            );
        }
        await updateClient({
          id: editClient.id,
          adminUpdate: {
            updatedBy: userEmail,
            updatedAt: DateTime.now().toISO(),
          },
          ...changesToSend,
        });
        // Refetch all clients and update Redux
        const allClients = await getAllClients(false);
        dispatch(setAllClients(allClients));
        setConfirmationOpen(true);
      } else {
        navigateToClientManagement();
      }
    } catch (error) {
      handleOpenSnackbar(error.message);
    }
  };

  const handleSaveClick = async (formik) => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const stepName = lookupStepForError(
        Object.keys(errors)[0],
        stepFieldsMap
      );
      handleOpenSnackbar(`Please fix all errors at the ${stepName} step`);
      return;
    }
    formik.handleSubmit();
    setCompleted(true);
  };

  return (
    <Formik
      initialValues={{ ...defaultValues, ...editClient }}
      enableReinitialize={true}
      validationSchema={clientValidationSchema}
      onSubmit={(values) => handleClientSave(values)}
    >
      {(formik) => {
        const clientSteps = getClientSteps(formik.values.settings);
        const StepComponent = stepComponents[clientSteps[page]];
        const currentStepFields = stepFieldsMap[clientSteps[page]] || [];

        return (
          <form
            onSubmit={formik.handleSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.nodeName !== "TEXTAREA") {
                e.preventDefault();
              }
            }}
          >
            <ReviewUpdateDialog
              open={updateDialogOpen}
              onClose={() => setUpdateDialogOpen(false)}
              onConfirm={() => processClientChanges()}
              account={"Client"}
              reviewPage={<ReviewPage formik={formik} />}
              finishLoading={confirmationOpen}
            />
            <SuccessDialog
              onOpen={confirmationOpen}
              onClose={() => {
                setConfirmationOpen(false);
                navigateToClientManagement();
              }}
              accountName={formik.values.name}
              isUpdating={!!editClient}
              isClient={true}
            />
            <Box sx={commonStyles.pageBox}>
              <Typography sx={textStyles.title}>
                {editClient
                  ? `Edit Client: ${editClient.name}`
                  : "Create Client"}
              </Typography>

              {/* Stepper or Tabs */}
              {editClient ? (
                <PageTabs
                  formik={formik}
                  clientSteps={clientSteps}
                  stepFieldsMap={stepFieldsMap}
                  currentStepFields={currentStepFields}
                  page={page}
                  setPage={setPage}
                  handleOpenSnackbar={handleOpenSnackbar}
                />
              ) : (
                <MNStepper
                  steps={clientSteps}
                  activeStep={page}
                  latestStepCompleted={latestStepCompleted}
                  onStepClick={(stepIndex) =>
                    handleNewStepClick(
                      formik,
                      currentStepFields,
                      stepIndex,
                      setPage,
                      setLatestStepCompleted,
                      handleOpenSnackbar
                    )
                  }
                />
              )}

              {/* Step Content */}
              <Fade in={true} key={page} timeout={300}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {StepComponent && StepComponent({ formik })}
                </Box>
              </Fade>

              {/* Form Buttons */}
              <NavigationButtons
                page={page}
                editAccount={editClient}
                lastPageNumber={clientSteps.length - 1}
                cancelClick={navigateToClientManagement}
                nextClick={() =>
                  handleNewStepClick(
                    formik,
                    currentStepFields,
                    page + 1,
                    setPage,
                    setLatestStepCompleted,
                    handleOpenSnackbar
                  )
                }
                backClick={() => setPage(page - 1)}
                saveClick={() => handleSaveClick(formik)}
                loading={saving}
              />
            </Box>
            <ConfirmLeaveModal
              onlyBlockWhen={
                !isEqual(formik.values, formik.initialValues) && !completed
              }
            />
          </form>
        );
      }}
    </Formik>
  );
};

export default ClientSettings;
